import { Typography, makeStyles } from '@material-ui/core';
import Button from 'components/Button/Button';
import { mdiVideo } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Refresher from 'components/Refresher/Refresher';
import {
  selectActiveCallForProject,
  selectIsUserInProjectCall,
} from 'components/ChatDialog/store/selectors/activeCalls.selectors';
import { setVideoChatOpen } from 'components/ChatDialog/store/slices/chatSlice';
import { openChatDialogForProject } from 'components/ChatDialog/store/thunks/openChatDialogForProject';
import { PROJECT_CHAT_ACCESS } from 'scenes/Permissions/constants/permissions';
import { selectIsRequiredPermissionByProjectId } from 'scenes/Permissions/store/selectors/personalPermissionsSelectorsByProjectId';
import hoursToTime2 from 'utils/hoursToTime2';
import isFeatureEnabled, { Feature } from 'utils/isFeatureEnabled';
import momentDiffHours from 'utils/momentDiffHours';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    width: '100%',
    gap: theme.spacing(1),
  },
  banner: {
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  row: {
    padding: theme.spacing(0.75, 1),
  },
  text: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
  },
  label: {
    ...theme.typography.fontPanton,
    fontSize: '0.9rem',
  },
  labelRow: {
    ...theme.typography.fontPanton,
    fontSize: '0.8rem',
  },
  // Monospace digits so the running timer stays fixed-width and doesn't jump
  // as the numbers change (Panton has no tabular figures).
  elapsed: {
    fontFamily: "'Roboto Mono', 'Menlo', 'Consolas', monospace",
    fontVariantNumeric: 'tabular-nums',
    fontSize: '0.75rem',
    opacity: 0.9,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.common.white,
    flexShrink: 0,
    animation: '$pulse 1.6s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.25 },
  },
  joinButton: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.success.main,
    flexShrink: 0,
    // Keep the text colour on hover; the app button's own overlay darkens it.
    '&:hover': {
      color: theme.palette.success.main,
    },
  },
}));

/**
 * Shows that a video call is ongoing in a project's main chat thread, with a
 * button that opens the call's preview/lobby.
 *
 * Renders nothing unless there is a live call and the viewer can actually join
 * it. `banner` is the prominent dashboard form; `row` is the compact strip used
 * inside a projects-list row.
 */
const OngoingCallIndicator = ({ project, variant = 'banner' }) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const dispatch = useDispatch();

  const projectId = project?.id;

  const activeCallSelector = useMemo(
    () => selectActiveCallForProject(projectId),
    [projectId]
  );
  const activeCall = useSelector(activeCallSelector);

  const isInCallSelector = useMemo(
    () => selectIsUserInProjectCall(projectId),
    [projectId]
  );
  const isUserInCall = useSelector(isInCallSelector);

  const permissionSelector = useMemo(
    () => selectIsRequiredPermissionByProjectId(projectId),
    [projectId]
  );
  const isPermissionToAccessProjectChat = useSelector((state) =>
    projectId ? permissionSelector(state, PROJECT_CHAT_ACCESS) : false
  );

  if (!project || !activeCall) return null;
  if (!isPermissionToAccessProjectChat) return null;
  if (
    !isFeatureEnabled(Feature.PROJECT_CHAT, project) ||
    !isFeatureEnabled(Feature.VIDEO_CHAT, project)
  ) {
    return null;
  }

  // The same sequence the ?openVideoChat deep link uses: open the project's
  // chat, then the call pane, which lands in the preview rather than joining.
  const handleJoin = () => {
    dispatch(openChatDialogForProject({ project }));
    dispatch(setVideoChatOpen(true));
  };

  const isBanner = variant === 'banner';

  return (
    <div
      className={`${classes.root} ${isBanner ? classes.banner : classes.row}`}
    >
      <div className={classes.text}>
        <span className={classes.dot} />
        <Icon path={mdiVideo} size={isBanner ? 0.9 : 0.75} />
        <Typography
          className={isBanner ? classes.label : classes.labelRow}
          noWrap
        >
          {t('chat.video_call_is_active')}
        </Typography>
        {activeCall.createdAt && (
          <Refresher
            render={() => (
              <Typography className={classes.elapsed}>
                {hoursToTime2(momentDiffHours(activeCall.createdAt))}
              </Typography>
            )}
          />
        )}
      </div>
      <Button
        size="small"
        className={classes.joinButton}
        onClick={handleJoin}
      >
        {isUserInCall ? t('common.open') : t('common.join')}
      </Button>
    </div>
  );
};

export default OngoingCallIndicator;
